'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { useShopifyCartContext } from '../onboarding/OnboardingFlowContainer/ShopifyCartProvider';
import { useSamplerCartContext } from '../onboarding/SamplerBoxFlowContainer/SamplerCartProvider';
import { ApiResult } from '../api';
import { shared } from '../config';
import { OFFER_URL } from './constants';
import {
  getFormattedOfferAmountByType,
  isQueryOfferLoading,
  resolveActiveOfferCode,
} from './helpers';

export const isSamplerBoxPath = pathname =>
  Boolean(pathname?.includes('/sampler-box'));

export const isFlatRatePath = pathname =>
  Boolean(pathname?.startsWith('/flat-rate'));

export const resolveOfferCartContext = (pathname, shopifyCart, samplerCart) =>
  isSamplerBoxPath(pathname) ? samplerCart : shopifyCart;

// Sampler cart promo is owned by applySamplerOfferCode. Re-applying the URL
// code here puts back a valid-but-not-applicable coupon that init just cleared.
export const shouldApplyQueryOfferToCart = pathname =>
  !isSamplerBoxPath(pathname);

export const resolveRouterOfferCode = offerCode => {
  if (Array.isArray(offerCode)) return offerCode[0] || null;
  return offerCode || null;
};

export const resolveSearchParamsOfferCode = searchParams =>
  searchParams?.get?.('offer_code') || null;

const defaultContextValue = {
  addPromoCode: () => Promise.resolve(null),
  formattedAmount: '50%',
  type: undefined,
  amount: undefined,
  code: undefined,
  message: undefined,
  category: undefined,
  defaultOfferCode: shared.DEFAULT_OFFER_CODE || 'DEFAULT',
};

// Flat Rate pricing takes no offers. formattedAmount is '' (not undefined) so
// consumers that fall back with `?? '50%'` don't show the default offer, and
// amount is 0 so calculateDiscountedPrice returns the full price.
export const FLAT_RATE_OFFER_STATE = {
  addPromoCode: () => Promise.resolve(null),
  formattedAmount: '',
  type: null,
  amount: 0,
  code: undefined,
  message: undefined,
  category: undefined,
  defaultOfferCode: undefined,
};

export const ShopifyOfferContext = createContext(defaultContextValue);

// Custom hook to query offer details
const useQueryOfferDetails = () => {
  const router = useRouter();
  const queryOfferCode = router.isReady
    ? resolveRouterOfferCode(router.query.offer_code)
    : null;
  const [invalidQueryCodes, setInvalidQueryCodes] = useState([]);

  const { data, error } = useSWR(
    queryOfferCode && !invalidQueryCodes.includes(queryOfferCode)
      ? `${OFFER_URL}${queryOfferCode}/`
      : null, // SHOPIFY todo: update to v4 coupons endpoint
  );

  useEffect(() => {
    if (error && queryOfferCode) {
      setInvalidQueryCodes(codes =>
        codes.includes(queryOfferCode) ? codes : [...codes, queryOfferCode],
      );
    }
  }, [error, queryOfferCode]);

  return {
    queryOfferCode,
    offerDetails: data,
    error,
    invalidQueryCodes,
    isLoading: isQueryOfferLoading({
      queryOfferCode,
      invalidQueryCodes,
      error,
      data,
    }),
  };
};

export const ShopifyOfferProvider = ({ children }) => {
  const router = useRouter();
  const shopifyCart = useShopifyCartContext();
  const samplerCart = useSamplerCartContext();
  const cartContext = resolveOfferCartContext(
    router.pathname,
    shopifyCart,
    samplerCart,
  );
  const isSampler = isSamplerBoxPath(router.pathname);
  const isFlatRate = isFlatRatePath(router.pathname);
  const shouldApplyQueryOffer = shouldApplyQueryOfferToCart(router.pathname);

  // Ensure cartContext is never null/undefined
  const safeCartContext = cartContext || {
    cartId: null,
    error: false,
    couponCode: null,
    isLoading: true,
    addPromo: () => Promise.resolve(null),
  };

  const {
    cartId,
    error: cartError,
    couponCode: cartOfferCode,
    isLoading: isCartLoading,
    addPromo,
  } = safeCartContext;
  const isCartReady =
    (safeCartContext?.cart_id || safeCartContext?.cart?.cart_id) &&
    !cartError &&
    !isCartLoading;
  const {
    queryOfferCode,
    offerDetails: queryOfferDetails,
    isLoading: isQueryLoading,
  } = useQueryOfferDetails();

  const [invalidCodes, setInvalidCodes] = useState([]);

  const offerCode = useMemo(
    () =>
      resolveActiveOfferCode({
        isQueryLoading,
        isCartLoading,
        queryOfferDetails,
        queryOfferCode,
        cartOfferCode,
        defaultOfferCode: shared.DEFAULT_OFFER_CODE,
        allowDefault: !isSampler,
        preferCartOffer: isSampler,
      }),
    [
      cartOfferCode,
      isCartLoading,
      isQueryLoading,
      isSampler,
      queryOfferCode,
      queryOfferDetails,
    ],
  );

  const { data: offerDetails } = useSWR(
    offerCode ? `${OFFER_URL}${offerCode}/` : null,
    {
      revalidateIfStale: false,
    },
  );

  useEffect(() => {
    if (
      shouldApplyQueryOffer &&
      isCartReady &&
      cartId &&
      offerCode &&
      offerCode !== cartOfferCode &&
      !invalidCodes.includes(offerCode)
    ) {
      addPromo(offerCode)
        .then(result => {
          ApiResult.ifError(result, () => {
            setInvalidCodes(codes => [...codes, offerCode]);
          });
        })
        .catch(() => {
          setInvalidCodes(codes => [...codes, offerCode]);
        });
    }
  }, [
    shouldApplyQueryOffer,
    cartId,
    cartOfferCode,
    invalidCodes,
    offerCode,
    addPromo,
    isCartReady,
  ]);

  const offerState = useMemo(() => {
    if (isFlatRate) return FLAT_RATE_OFFER_STATE;

    const baseState = {
      addPromoCode: addPromo || (() => Promise.resolve(null)),
      formattedAmount: '50%',
      defaultOfferCode: shared.DEFAULT_OFFER_CODE || 'DEFAULT',
    };

    if (offerDetails) {
      const formattedAmount =
        getFormattedOfferAmountByType(offerDetails) || '50%';
      return {
        ...baseState,
        ...offerDetails,
        formattedAmount,
        defaultOfferCode: shared.DEFAULT_OFFER_CODE || 'DEFAULT',
      };
    }

    return baseState;
  }, [addPromo, isFlatRate, offerDetails]);

  return (
    <ShopifyOfferContext.Provider value={offerState}>
      {children}
    </ShopifyOfferContext.Provider>
  );
};

export const useShopifyOfferContext = () => useContext(ShopifyOfferContext);
